import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Helmet } from '@dr.pogodin/react-helmet'

import * as datefns from 'date-fns'
import { sv } from 'date-fns/locale'

import {
    Grid,
    Card as MuiCard,
    Divider as MuiDivider,
    Paper as MuiPaper,
    Typography as MuiTypography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    CardHeader,
    Button,
} from '@mui/material'
import { spacing } from '@mui/system'
import { formatDateYYYYMMDD } from '../../../../packages/utils/numberFormatters'
import DashboardApi from '../../../../api/DashboardApi'
import CacheApi from '../../../../api/CacheApi'
import { DeployInfo } from './DeployInfo'

const Paper = styled(MuiPaper)(spacing)
// import { green, red } from '@mui/material/colors'
const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Typography = styled(MuiTypography)(spacing)

const dashboardApi = new DashboardApi()

function InstrumentsMissingCompany() {
    const [instrumentsMissingCompany, setInstrumentsMissingCompany] = useState(null)
    useEffect(() => {
        const fetcher = async () => {
            const result = await dashboardApi.find('instruments-missing-companies')
            setInstrumentsMissingCompany(result)
        }
        fetcher()
    }, [setInstrumentsMissingCompany])

    let content = []
    content.push(
        <Typography key={content.length} variant='caption' gutterBottom sx={{ mx: 4 }}>
            Här är instrument som saknar bolag (company_id) i databasen. Endast de senaste 10 visas.
        </Typography>,
    )
    if (instrumentsMissingCompany) {
        if (instrumentsMissingCompany.length === 0) {
            content.push(<Typography key={content.length}>Inga instrument utan bolag. Toppen!</Typography>)
        } else {
            content.push(
                <Table key={content.length} size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Instrument id</TableCell>
                            <TableCell>Instrumentnamn</TableCell>
                            <TableCell>Skapat datum</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instrumentsMissingCompany.map((instrument) => (
                            <TableRow key={instrument.id}>
                                <TableCell>{instrument.id}</TableCell>
                                <TableCell>{instrument.name}</TableCell>
                                <TableCell>{formatDateYYYYMMDD(instrument.createdAt)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>,
            )
        }
    }
    return (
        <Card mb={6}>
            <CardHeader title='Instrument utan bolag'></CardHeader>
            <Paper>{content}</Paper>
        </Card>
    )
}

/**
 * Show cache status, including which queries are cached and since when.
 * Also includes a button to clear the cache.
 */
// import { useState, useEffect } from 'react'
// import { Button, Card, CardHeader, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
// import datefns from 'date-fns'
// import sv from 'date-fns/locale/sv' // Assuming you have imported locale for date formatting

function CacheStatus() {
    const [buttonDisabled, setButtonDisabled] = useState(false)
    const [timer, setTimer] = useState(0)
    const [cacheStatus, setCacheStatus] = useState(null)
    const dashboardApi = new DashboardApi()
    const cacheApi = new CacheApi()

    const fetcher = React.useCallback(async () => {
        const result = await dashboardApi.find('cache-status')
        setCacheStatus(result)
    }, [setCacheStatus])

    const handleButtonClick = () => {
        // Disable the button
        setButtonDisabled(true)

        // Invalidate cache
        cacheApi.invalidate()

        // Start a 60-second countdown
        setTimer(60)
    }

    useEffect(() => {
        fetcher()
    }, [fetcher])

    // Update the countdown timer every second if the timer is active
    useEffect(() => {
        let countdown
        if (timer > 0) {
            countdown = setInterval(() => {
                setTimer((prev) => prev - 1)
            }, 1000)
        }

        // When timer reaches 0, enable button and reset label
        if (timer === 0 && buttonDisabled) {
            fetcher()
            setButtonDisabled(false)
        }

        // Clear interval on cleanup
        return () => clearInterval(countdown)
    }, [timer, buttonDisabled, setCacheStatus])

    let content = []
    content.push(
        <Typography key={content.length} variant='caption' gutterBottom sx={{ mx: 4 }}>
            Cachade queries
        </Typography>,
    )

    // Add the button above the table
    content.push(
        <Button
            key={content.length}
            variant='contained'
            color='primary'
            onClick={handleButtonClick}
            disabled={buttonDisabled}
            sx={{ my: 2 }}
        >
            {buttonDisabled ? `Ladda om cache (${timer}s)` : 'Ladda om cache'}
        </Button>,
    )

    if (cacheStatus) {
        if (cacheStatus.length === 0) {
            content.push(
                <Typography key={content.length} variant='body1' sx={{ mx: 4 }}>
                    Inga cachade queries.
                </Typography>,
            )
        } else {
            content.push(
                <Table key={content.length} size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Objekt</TableCell>
                            <TableCell>Senast cachad</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cacheStatus.map((cache) => (
                            <TableRow key={cache.label}>
                                <TableCell>{cache.label}</TableCell>
                                <TableCell>
                                    {cache.creationDate &&
                                        datefns.format(new Date(cache.creationDate), 'yyyy-MM-dd HH:mm', {
                                            locale: sv,
                                        })}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>,
            )
        }
    }

    return (
        <Card mb={6}>
            <CardHeader title='Cache status'></CardHeader>
            <Paper>{content}</Paper>
        </Card>
    )
}

function Default() {
    return (
        <React.Fragment>
            <Helmet title='Admin Dashboard' />
            <Grid
                container
                spacing={6}
                sx={{
                    justifyContent: 'space-between',
                }}
            >
                <Grid>
                    <Typography variant='h3' gutterBottom>
                        Admin Dashboard
                    </Typography>
                    <DeployInfo />
                </Grid>
            </Grid>
            <Divider my={6} />
            <Grid container spacing={6}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 8 }}>
                    <InstrumentsMissingCompany />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
                    <CacheStatus />
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default Default
